// src/pages/MultilingualDesktopPublishing.js
import React from 'react';
import { NavLink } from 'react-router-dom';

const MultilingualDesktopPublishing = () => (
  <>
    {/* Breadcrumb */}
    <div
      className="optech-breadcrumb"
      style={{ backgroundImage: "url(/assets/images/breadcrumb/breadcrumb.png)" }}
    >
      <div className="container">
        <h1 className="post__title">Multilingual Desktop Publishing</h1>
        <nav className="breadcrumbs">
          <ul>
            <li>
              <NavLink to="/services">AI Service</NavLink>
            </li>
            <li aria-current="page">Multilingual Desktop Publishing</li>
          </ul>
        </nav>
        <p className="text-white pt-5">
          Multilingual Desktop Publishing (DTP) supports the cohesive presentation of documents across multiple languages and cultural contexts. By blending meticulous design with linguistic knowledge, R A Inc ensures that each language version maintains both visual appeal and textual precision.
        </p>
      </div>
    </div>

    {/* Service Section */}
    <div className="section optech-section-padding">
      <div className="container">
        <div className="row">
          {/* Left Content Area */}
          <div className="col-lg-8">
            <div className="optech-service-details-wrap">
              <img
                data-aos="fade-up"
                data-aos-duration="800"
                src="/assets/images/service/multilingual-dtp.jpg"
                alt="Multilingual DTP Services"
                className="mb-4 w-100"
              />

              {/* Overview */}
              <div className="optech-service-details-item">
                <h3>Overview</h3>
                <p>
                  Handling text in multiple languages can involve grappling with script direction changes (left-to-right vs. right-to-left), expansion or contraction of text blocks, and font compatibility issues. Even straightforward translations may disrupt a carefully laid-out page if not handled properly. Our multilingual DTP process tackles these hurdles, so every language variant is on par with the original’s standard of excellence.
                </p>
              </div>

              {/* Main Components */}
              <div className="optech-service-details-item">
                <h3>Main Components</h3>
                <div className="optech-icon-list">
                  <ul>
                    <li>
                      <i className="ri-check-line"></i>
                      <strong>Format Conversion:</strong> Transforming files to handle multilingual typography without losing design integrity.
                    </li>
                    <li>
                      <i className="ri-check-line"></i>
                      <strong>E-Learning Localization:</strong> Reworking interactive modules to accommodate new languages and cultural elements.
                    </li>
                    <li>
                      <i className="ri-check-line"></i>
                      <strong>File Preparation & Engineering:</strong> Creating file structures that integrate with translation memories.
                    </li>
                    <li>
                      <i className="ri-check-line"></i>
                      <strong>OCR & Proofreading:</strong> Converting physical documents into editable formats and double-checking accuracy.
                    </li>
                    <li>
                      <i className="ri-check-line"></i>
                      <strong>Vectorization:</strong> Turning images or diagrams into scalable vectors suitable for large or small prints.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Processing Table */}
              <div className="optech-service-details-item">
                <h3>Common Multilingual DTP Challenges</h3>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Challenge</th>
                      <th>Our Approach</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Text Expansion</td>
                      <td>Adjust spacing, font sizes, or layout to accommodate changes</td>
                    </tr>
                    <tr>
                      <td>Right-to-Left Scripts</td>
                      <td>Use specialized software features for accurate text flow</td>
                    </tr>
                    <tr>
                      <td>Special Characters</td>
                      <td>Employ Unicode-compliant fonts and thorough testing</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Highlights */}
              <div className="optech-service-details-item">
                <h3>Highlights</h3>
                <ul className="list-unstyled">
                  <li><i className="ri-check-line"></i>Our process allows simultaneous work on multiple language layouts, speeding up project timelines.</li>
                  <li><i className="ri-check-line"></i>Adjustments in color palettes and imagery align with local preferences.</li>
                  <li><i className="ri-check-line"></i>Detailed final verification to guarantee consistency in style, grammar, and brand positioning.</li>
                </ul>
              </div>

              {/* Main Points */}
              <div className="optech-service-details-item">
                <h3>Main Points</h3>
                <ul className="list-unstyled">
                  <li><i className="ri-check-line"></i><strong>Localization Expertise:</strong> Professional handling of culturally specific content adaptations.</li>
                  <li><i className="ri-check-line"></i><strong>Technical Compatibility:</strong> Support for diverse file types and platform requirements.</li>
                  <li><i className="ri-check-line"></i><strong>Efficiency:</strong> End-to-end support for faster multilingual publication.</li>
                </ul>
              </div>

              {/* Conclusion */}
              <div className="optech-service-details-item">
                <h3>Conclusion</h3>
                <p>
                  Publishing the same piece in different languages demands a fusion of linguistic savvy and design insight. R A Inc’s multilingual DTP offerings deliver polished, culturally relevant layouts, enabling you to publish content that resonates with local audiences worldwide without compromising the visual impact.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar Section */}
          <div className="col-lg-4">
            <div className="optech-service-sidebar">
              <div className="optech-service-menu">
                <ul>
                  {[
                    { name: 'Multimedia & DTP Services',       slug: '/multimedia-and-dtp-services' },
                    { name: 'Audio & Video Transcription',     slug: '/audio-and-video-transcription' },
                    { name: 'Desktop Publishing Services',     slug: '/desktop-publishing-services' },
                    { name: 'Subtitle Services',               slug: '/subtitling' },
                    { name: 'Multilingual Desktop Publishing', slug: '/multilingual-desktop-publishing' },
                    { name: 'Multimedia & DTP Tools',          slug: '/multimedia-and-dtp-tools' }
                  ].map(service => (
                    <li key={service.slug}>
                      <NavLink to={service.slug}>
                        {service.name} <i className="ri-arrow-right-up-line" />
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className="optech-service-contact"
                data-aos="fade-up"
                data-aos-duration="800"
                style={{ backgroundImage: "url(/assets/images/service/bg.png)" }}
              >
                <div className="optech-service-contact-icon">
                  <img src="/assets/images/service/icon.svg" alt="" />
                </div>
                <h3>Need Multilingual DTP Support?</h3>
                <p>Collaborate with our team for flawless layout adaptation across multiple languages and platforms.</p>
                <NavLink className="optech-default-btn" data-text="Get in Touch" to="/contact-us">
                  <span className="btn-wraper">Get in Touch</span>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Call To Action */}
    <div
      className="section bg-cover optech-section-padding"
      style={{ backgroundImage: "url(/assets/images/cta/cta-bg2.png)" }}
    >
      <div className="container">
        <div className="optech-cta-wrap">
          <div className="optech-cta-content center">
            <h2>Let's Translate Your Vision Into Every Language</h2>
            <p>
              Whether you're expanding your business internationally or need multilingual content for local markets, we provide solutions tailored to your goals, audience, and tone.
            </p>
            <div className="optech-extra-mt" data-aos="fade-up" data-aos-duration="800">
              <NavLink className="optech-default-btn" data-text="Let’s Start a Project" to="/contact-us">
                <span className="btn-wraper">Let’s Start a Project</span>
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
);

export default MultilingualDesktopPublishing;